import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useApi } from "@/lib/api";
import { PageHeader, Panel, PageLoader, StatusBadge, EmptyState, Btn, TextInput, TextArea } from "@/lib/ui";
import { MediaPicker, MediaImg } from "@/components/MediaPicker";
import { Images, Plus, Trash2, Star, DownloadCloud, Globe, Archive, PencilLine } from "lucide-react";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const STATUSES = ["draft", "published", "archived"];
const TONE = { draft: "muted", published: "ok", archived: "err" };
const BRANDS = ["", "Gopi Gadhvi Group", "Monarch Estates", "Monarch Design Studio", "The Art Forest", "The Wishing Well"];
const EMPTY = { title: "", image_url: "", category: "", brand: "", caption: "", featured: false, sort_order: 0, status: "published" };

function Editor({ item, onClose }) {
  const api = useApi();
  const qc = useQueryClient();
  const isNew = !item?.id;
  const [form, setForm] = useState(EMPTY);
  useEffect(() => { setForm(item?.id ? { ...EMPTY, ...item } : EMPTY); }, [item?.id]); // eslint-disable-line
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inval = () => { qc.invalidateQueries({ queryKey: ["gallery"] }); qc.invalidateQueries({ queryKey: ["gallery-stats"] }); };

  const payload = () => {
    const K = ["title", "image_url", "category", "brand", "caption", "featured", "sort_order", "status"];
    const p = {}; K.forEach((k) => { p[k] = form[k]; }); return p;
  };
  const save = useMutation({
    mutationFn: () => isNew ? api.post("/cms/gallery", payload()) : api.patch(`/cms/gallery/${item.id}`, payload()),
    onSuccess: () => { toast.success(isNew ? "Image added" : "Saved"); inval(); onClose(); },
    onError: (e) => toast.error(e.message),
  });
  const del = useMutation({
    mutationFn: () => api.del(`/cms/gallery/${item.id}`),
    onSuccess: () => { toast.success("Deleted"); inval(); onClose(); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div className="space-y-4" data-testid="gallery-editor">
      <div>
        <span className="text-xs text-muted-foreground mb-1.5 block">Image</span>
        {form.image_url && <MediaImg src={form.image_url} alt="" className="mb-2 h-40 w-full rounded-md border border-border object-cover" testid="gallery-image-preview" />}
        <div className="flex items-center gap-2">
          <input className="w-full h-9 rounded-md border border-border bg-surface px-3 text-sm" placeholder="Image URL or pick from Media Library"
            value={form.image_url} onChange={(e) => set("image_url", e.target.value)} data-testid="gallery-image-url" />
          <MediaPicker folder="Gallery" onPick={(url) => set("image_url", url)} />
        </div>
      </div>
      <TextInput label="Title" value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="gallery-title" />
      <div className="grid grid-cols-2 gap-3">
        <TextInput label="Category" value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="gallery-category" />
        <div>
          <span className="text-xs text-muted-foreground mb-1.5 block">Brand</span>
          <Select value={form.brand} onValueChange={(v) => set("brand", v === "none" ? "" : v)}>
            <SelectTrigger data-testid="gallery-brand"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>{BRANDS.map((b) => <SelectItem key={b || "none"} value={b || "none"}>{b || "None"}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <TextArea label="Caption" rows={2} value={form.caption} onChange={(e) => set("caption", e.target.value)} data-testid="gallery-caption" />
      <div className="grid grid-cols-2 gap-3">
        <div>
          <span className="text-xs text-muted-foreground mb-1.5 block">Status</span>
          <Select value={form.status} onValueChange={(v) => set("status", v)}>
            <SelectTrigger data-testid="gallery-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`gallery-status-${s}`}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <TextInput label="Sort order" type="number" value={form.sort_order} onChange={(e) => set("sort_order", parseInt(e.target.value || "0", 10))} data-testid="gallery-sort" />
      </div>
      <div className="flex items-center justify-between border border-border rounded-md px-3 py-2.5">
        <div className="text-sm font-medium">Featured</div>
        <Switch checked={form.featured} onCheckedChange={(v) => set("featured", v)} data-testid="gallery-featured-switch" />
      </div>
      <div className="flex items-center gap-2 border-t border-border pt-4">
        <Btn onClick={() => save.mutate()} disabled={save.isPending || !form.title.trim()} data-testid="gallery-save-btn">{save.isPending ? "Saving…" : isNew ? "Add image" : "Save"}</Btn>
        {!isNew && (
          <AlertDialog>
            <AlertDialogTrigger asChild><Btn variant="danger" data-testid="gallery-delete-btn"><Trash2 className="h-4 w-4" /> Delete</Btn></AlertDialogTrigger>
            <AlertDialogContent className="bg-surface border-border">
              <AlertDialogHeader><AlertDialogTitle>Delete this image?</AlertDialogTitle><AlertDialogDescription>This removes “{form.title}” from the gallery. Cannot be undone.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel data-testid="gallery-delete-cancel">Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del.mutate()} data-testid="gallery-delete-confirm" className="bg-err text-white hover:bg-err/90">Delete</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </div>
  );
}

function ImportButton() {
  const api = useApi();
  const qc = useQueryClient();
  const imp = useMutation({
    mutationFn: () => api.post("/cms/gallery/import"),
    onSuccess: (d) => { toast.success(`Imported ${d.imported} image(s) · ${d.skipped} skipped`); qc.invalidateQueries({ queryKey: ["gallery"] }); qc.invalidateQueries({ queryKey: ["gallery-stats"] }); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild><Btn variant="outline" size="sm" data-testid="gallery-import-btn"><DownloadCloud className="h-4 w-4" /> Import from live site</Btn></AlertDialogTrigger>
      <AlertDialogContent className="bg-surface border-border">
        <AlertDialogHeader><AlertDialogTitle>Import gallery from the live website?</AlertDialogTitle><AlertDialogDescription>Copies existing images from gopigadhvi.com into the Command Centre. Nothing is overwritten and the live site is unchanged.</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel data-testid="gallery-import-cancel">Cancel</AlertDialogCancel><AlertDialogAction onClick={() => imp.mutate()} disabled={imp.isPending} data-testid="gallery-import-confirm">{imp.isPending ? "Importing…" : "Import"}</AlertDialogAction></AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default function Gallery() {
  const api = useApi();
  const qc = useQueryClient();
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const { data: stats } = useQuery({ queryKey: ["gallery-stats"], queryFn: () => api.get("/cms/gallery/stats") });
  const { data: items = [], isLoading } = useQuery({ queryKey: ["gallery", filter], queryFn: () => api.get(`/cms/gallery${filter === "all" ? "" : `?status=${filter}`}`) });
  const quick = useMutation({
    mutationFn: ({ id, status }) => api.patch(`/cms/gallery/${id}`, { status }),
    onSuccess: () => { toast.success("Updated"); qc.invalidateQueries({ queryKey: ["gallery"] }); qc.invalidateQueries({ queryKey: ["gallery-stats"] }); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div data-testid="gallery-page">
      <PageHeader kicker="Content · Website CMS" title="Gallery">
        <ImportButton />
        <Btn size="sm" onClick={() => setEditing({})} data-testid="gallery-new-btn"><Plus className="h-4 w-4" /> Add image</Btn>
      </PageHeader>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {["all", ...STATUSES].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`gallery-filter-${s}`}
            className={cn("text-xs rounded-full px-3 py-1.5 border transition-colors capitalize",
              filter === s ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:text-foreground")}>
            {s}{stats ? ` · ${s === "all" ? (stats.total ?? 0) : (stats[s] ?? 0)}` : ""}
          </button>
        ))}
      </div>

      {isLoading ? <PageLoader /> : items.length === 0 ? (
        <Panel className="p-10"><EmptyState icon={Images} title="No images yet" hint="Add an image or import the gallery from the live website."
          action={<Btn size="sm" className="mt-2" onClick={() => setEditing({})} data-testid="gallery-empty-new"><Plus className="h-4 w-4" /> Add image</Btn>} /></Panel>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="gallery-grid">
          {items.map((it) => (
            <Panel key={it.id} className="overflow-hidden group" data-testid={`gallery-card-${it.id}`}>
              <button onClick={() => setEditing(it)} className="block w-full aspect-[4/3] bg-accent/40 overflow-hidden" data-testid={`gallery-edit-${it.id}`}>
                {it.image_url ? <MediaImg src={it.image_url} alt={it.title} className="h-full w-full object-cover transition-transform group-hover:scale-105" fallback={<div className="h-full w-full grid place-items-center text-muted-foreground"><Images className="h-8 w-8" /></div>} /> : <div className="h-full w-full grid place-items-center text-muted-foreground"><Images className="h-8 w-8" /></div>}
              </button>
              <div className="p-3 space-y-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  {it.featured && <Star className="h-3.5 w-3.5 text-primary shrink-0" />}
                  <div className="text-sm font-medium truncate flex-1">{it.title}</div>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <StatusBadge tone={TONE[it.status]} dot>{it.status}</StatusBadge>
                  <div className="flex items-center gap-1">
                    {it.status !== "published" ? (
                      <Btn variant="ghost" size="icon" title="Publish" onClick={() => quick.mutate({ id: it.id, status: "published" })} data-testid={`gallery-publish-${it.id}`}><Globe className="h-4 w-4" /></Btn>
                    ) : (
                      <Btn variant="ghost" size="icon" title="Archive" onClick={() => quick.mutate({ id: it.id, status: "archived" })} data-testid={`gallery-archive-${it.id}`}><Archive className="h-4 w-4" /></Btn>
                    )}
                    <Btn variant="ghost" size="icon" title="Edit" onClick={() => setEditing(it)} data-testid={`gallery-open-${it.id}`}><PencilLine className="h-4 w-4" /></Btn>
                  </div>
                </div>
              </div>
            </Panel>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-surface border-border max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit image" : "Add image"}</DialogTitle><DialogDescription>Manage a gallery image shown on the website.</DialogDescription></DialogHeader>
          {editing && <Editor item={editing} onClose={() => setEditing(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
